/**
 * useNavLayoutStore — user-defined sidebar nav grouping (module: `navGroups`).
 *
 * WHY THIS EXISTS
 *   The sidebar used to hardcode a four-section taxonomy (orient / execute /
 *   organize / strategy). That is a global assumption about how *everyone*
 *   thinks. The default is now flat and header-less; a user who wants sections
 *   turns on the `navGroups` module in the Store and defines their OWN groups
 *   here.
 *
 * ZERO REQUIRED SETUP (AGENTS.md)
 *   Module off, or on with `groups: []` → `resolveNavSections()` returns a
 *   single header-less section containing every visible item in its original
 *   order. Identical to the feature not existing.
 *
 * SHAPE
 *   groups: [{ id: uuid, label: string, itemPaths: string[] }]
 *   Nav items not listed in any group live in an implicit "ungrouped" bucket
 *   that renders LAST with NO header. There is no ungrouped group object —
 *   ungrouped is the absence of membership, which is why an item can never be
 *   lost: deleting a group simply stops claiming its paths.
 *
 * INVARIANT
 *   A path appears in AT MOST ONE group. Enforced on write (`moveItem` strips
 *   the path from every group before inserting) and on read (`_normalizeGroups`
 *   drops duplicates), so hand-edited / cloud-merged data self-heals.
 *
 * Storage: 'nav_groups' (SINGLE_DOC_KEYS in utils/storage.js)
 */
import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';

const STORAGE_KEY = 'nav_groups';

/** Section id for the implicit, header-less bucket of unfiled items. */
export const UNGROUPED_ID = '__ungrouped__';

/** Fallback label when a group is created without one. */
const DEFAULT_GROUP_LABEL = 'New group';

// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers (exported for tests + for the sidebar to use without the hook)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Coerce anything that came out of storage (or a foreign client) into a valid
 * groups array. Drops malformed entries, non-string paths and cross-group
 * duplicates rather than throwing — nav layout is cosmetic and must never be
 * able to break boot.
 */
export function normalizeGroups(raw) {
    const list = Array.isArray(raw) ? raw : Array.isArray(raw?.groups) ? raw.groups : [];
    const claimed = new Set();
    const out = [];

    for (const g of list) {
        if (!g || typeof g !== 'object') continue;
        if (typeof g.id !== 'string' || !g.id) continue;

        const itemPaths = [];
        const source = Array.isArray(g.itemPaths) ? g.itemPaths : [];
        for (const p of source) {
            if (typeof p !== 'string' || !p) continue;
            if (claimed.has(p)) continue; // invariant: at most one group per path
            claimed.add(p);
            itemPaths.push(p);
        }

        out.push({
            id: g.id,
            label: typeof g.label === 'string' && g.label.trim() ? g.label.trim() : DEFAULT_GROUP_LABEL,
            itemPaths,
        });
    }

    return out;
}

/**
 * Turn the full list of VISIBLE nav items into the render order the sidebar
 * paints: user groups first (in group order, items in the order the user filed
 * them), then everything unfiled, last, with no header.
 *
 * @param {Array}  groups  ordered group objects (this store's `groups`)
 * @param {Array}  items   visible nav items — objects carrying a `path`
 * @returns {Array<{ id: string, label: string|null, items: Array }>}
 *
 * Contract:
 *   - EVERY item in `items` appears in EXACTLY ONE returned section.
 *   - Sections with zero visible items are omitted, so disabling a module can
 *     never leave an orphan header behind.
 *   - The ungrouped section always comes last and always has `label: null`.
 *   - `groups: []` (or an empty/None module) → exactly one ungrouped section
 *     holding `items` untouched, in the caller's original order.
 */
export function resolveNavSections(groups, items) {
    const list = Array.isArray(items) ? items : [];
    const safeGroups = Array.isArray(groups) ? groups : [];

    if (safeGroups.length === 0) {
        return list.length ? [{ id: UNGROUPED_ID, label: null, items: list }] : [];
    }

    // path → item, for O(1) lookup. Items with no path can never be filed.
    const byPath = new Map();
    for (const item of list) {
        if (item && typeof item.path === 'string' && item.path && !byPath.has(item.path)) {
            byPath.set(item.path, item);
        }
    }

    const sections = [];
    const taken = new Set();

    for (const group of safeGroups) {
        const groupItems = [];
        for (const path of group.itemPaths || []) {
            if (taken.has(path)) continue;
            const item = byPath.get(path);
            if (!item) continue; // filed but not currently visible (module off)
            taken.add(path);
            groupItems.push(item);
        }
        if (groupItems.length) {
            sections.push({ id: group.id, label: group.label, items: groupItems });
        }
    }

    // Everything else, in the caller's original order, header-less, last.
    const ungrouped = list.filter(
        item => !(item && typeof item.path === 'string' && taken.has(item.path))
    );
    if (ungrouped.length) {
        sections.push({ id: UNGROUPED_ID, label: null, items: ungrouped });
    }

    return sections;
}

/** The group id currently holding `path`, or null when the item is unfiled. */
export function findGroupIdForPath(groups, path) {
    if (!path) return null;
    const found = (Array.isArray(groups) ? groups : []).find(g => (g.itemPaths || []).includes(path));
    return found ? found.id : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

function _read() {
    return normalizeGroups(storage.get(STORAGE_KEY, null));
}

function _persist(groups) {
    // Wrapped in an object so future nav-layout knobs can join without a
    // migration of the stored shape (a bare array would need one).
    storage.set(STORAGE_KEY, { groups });
}

const useNavLayoutStore = create((set, get) => ({
    groups: _read(),

    // ── Group CRUD ──

    /**
     * Append a new, empty group.
     * @returns {string} the new group's id (so callers can focus its input)
     */
    createGroup(label) {
        const id = crypto.randomUUID();
        const clean = typeof label === 'string' && label.trim() ? label.trim() : DEFAULT_GROUP_LABEL;
        const groups = [...get().groups, { id, label: clean, itemPaths: [] }];
        set({ groups });
        _persist(groups);
        return id;
    },

    /** Rename a group. Empty / whitespace-only labels are ignored (no-op). */
    renameGroup(id, label) {
        const clean = typeof label === 'string' ? label.trim() : '';
        if (!clean) return;
        const current = get().groups;
        if (!current.some(g => g.id === id)) return;
        const groups = current.map(g => (g.id === id ? { ...g, label: clean } : g));
        set({ groups });
        _persist(groups);
    },

    /**
     * Delete a group. Its items are NOT deleted — they simply stop being
     * claimed, so they re-home into the implicit ungrouped bucket on the next
     * resolve. This is why the store can never lose a nav item.
     */
    deleteGroup(id) {
        const current = get().groups;
        if (!current.some(g => g.id === id)) return;
        const groups = current.filter(g => g.id !== id);
        set({ groups });
        _persist(groups);
    },

    /** Move a group within the ordered list. Out-of-range indices are a no-op. */
    reorderGroups(fromIndex, toIndex) {
        const current = get().groups;
        const len = current.length;
        if (
            !Number.isInteger(fromIndex) || !Number.isInteger(toIndex) ||
            fromIndex < 0 || fromIndex >= len ||
            toIndex < 0 || toIndex >= len ||
            fromIndex === toIndex
        ) return;
        const groups = [...current];
        const [moved] = groups.splice(fromIndex, 1);
        groups.splice(toIndex, 0, moved);
        set({ groups });
        _persist(groups);
    },

    // ── Item assignment ──

    /**
     * File `path` into `toGroupId` at `index`, removing it from wherever it was.
     *
     * @param {string} path        nav item path (the item's identity)
     * @param {string|null} toGroupId  group id, or null / UNGROUPED_ID to unfile
     * @param {number} [index]     position inside the target group; defaults to
     *                             the end. Clamped into range.
     *
     * Idempotent: calling it twice with the same arguments produces the same
     * groups. Unknown `toGroupId` is a no-op (never silently unfiles).
     */
    moveItem(path, toGroupId, index) {
        if (typeof path !== 'string' || !path) return;
        const current = get().groups;

        const target = toGroupId && toGroupId !== UNGROUPED_ID
            ? current.find(g => g.id === toGroupId)
            : null;
        if (toGroupId && toGroupId !== UNGROUPED_ID && !target) return; // unknown group

        // Strip from every group first — this is the "at most one group" invariant.
        const stripped = current.map(g =>
            (g.itemPaths || []).includes(path)
                ? { ...g, itemPaths: g.itemPaths.filter(p => p !== path) }
                : g
        );

        let groups = stripped;
        if (target) {
            groups = stripped.map(g => {
                if (g.id !== target.id) return g;
                const paths = [...g.itemPaths];
                const at = Number.isInteger(index)
                    ? Math.max(0, Math.min(index, paths.length))
                    : paths.length;
                paths.splice(at, 0, path);
                return { ...g, itemPaths: paths };
            });
        }

        // Skip the write when nothing actually changed (idempotent re-moves).
        if (_sameGroups(current, groups)) return;

        set({ groups });
        _persist(groups);
    },

    // ── Read helpers ──

    /** Resolve the sidebar's render order. See `resolveNavSections`. */
    getSections(items) {
        return resolveNavSections(get().groups, items);
    },

    /** Group id currently holding `path`, or null when unfiled. */
    getGroupIdForPath(path) {
        return findGroupIdForPath(get().groups, path);
    },

    /** True when the user has defined at least one group. */
    hasGroups() {
        return get().groups.length > 0;
    },

    /** Re-read from storage (cloud pull / rehydrate). Also handy in tests. */
    hydrate() {
        set({ groups: _read() });
    },
}));

/** Structural equality on the groups array (order-sensitive). */
function _sameGroups(a, b) {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        const x = a[i];
        const y = b[i];
        if (x.id !== y.id || x.label !== y.label) return false;
        if (x.itemPaths.length !== y.itemPaths.length) return false;
        for (let j = 0; j < x.itemPaths.length; j++) {
            if (x.itemPaths[j] !== y.itemPaths[j]) return false;
        }
    }
    return true;
}

onRehydrate(() => {
    useNavLayoutStore.getState().hydrate();
});

export default useNavLayoutStore;
